import React from 'react';
import { Mail, MessageCircle, ShieldCheck, FileText, Scale } from 'lucide-react';
import { PublicLayout, PageContainer, SectionHeading } from '../components/layout/Layout';
import { Button, Card } from '../components/ui/Primitives';
import { SUPPORT_EMAIL, TELEGRAM_CHANNEL_URL } from '../data/constants';
import { useSupportEmail } from '../hooks/useSupportEmail';

export function ContactPage() {
  const supportEmail = useSupportEmail(SUPPORT_EMAIL);
  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '980px' }}>
        <SectionHeading
          tag="Contact"
          title="Contact PayRocky"
          subtitle="Reach our team for support, order questions, educational updates, and community conversations."
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '14px', marginBottom: '2rem' }}>
          <ContactCard
            icon={<Mail size={20} />}
            title="Email support"
            body="For account help, transaction questions, or general enquiries, send us an email and our team will respond as quickly as possible."
            actionLabel={supportEmail}
            onClick={() => { window.location.href = `mailto:${supportEmail}`; }}
          />
          <ContactCard
            icon={<MessageCircle size={20} />}
            title="Telegram community"
            body="Join our fast-growing educational and interactive Telegram community for updates, learning, and direct engagement."
            actionLabel="Join Telegram"
            onClick={() => window.open(TELEGRAM_CHANNEL_URL, '_blank', 'noopener,noreferrer')}
          />
        </div>

        <Card>
          <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--clr-accent)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
            Support hours
          </div>
          <div style={{ fontSize: '14px', color: 'var(--clr-text)', fontWeight: 700, marginBottom: '8px' }}>
            We are available to assist with transaction-related requests and general support.
          </div>
          <div style={{ fontSize: '14px', color: 'var(--clr-text-2)', lineHeight: 1.8, wordBreak: 'break-word' }}>
            Our team manually reviews transactions and support requests. Please include your order ID where possible so we can help faster.
          </div>
        </Card>
      </PageContainer>
    </PublicLayout>
  );
}

export function PrivacyPolicyPage() {
  const supportEmail = useSupportEmail(SUPPORT_EMAIL);
  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '980px' }}>
        <SectionHeading
          tag="Privacy Policy"
          title="Your privacy matters"
          subtitle="This page explains how PayRocky collects, uses, and protects customer information across the platform."
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Introduction"
          points={[
            'This site is owned and operated by LordRock Icon Ltd through PayRocky.com.',
            'PayRocky provides cryptocurrency buying and selling services, and your privacy on the internet is important to us.',
            'By using the website, you consent to the data practices described in this policy.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Collection of personal information"
          points={[
            'By using the website and its functions, you may provide information including your name, phone number, address, and email address.',
            'If you sign up or purchase services through PayRocky, we may also collect billing or related transaction information needed to process your order.',
            'PayRocky encourages you to review the privacy statements of third-party websites you visit from our platform. We are not responsible for the content or privacy practices of external websites.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Information we collect"
          points={[
            'Basic account details such as your name, email address, and phone number.',
            'Transaction records needed to process buy and sell orders on the platform.',
            'Information you provide through support requests, compliance checks, KYC, or payment proof uploads.',
            'For identity verification, PayRocky may collect and process your National Identification Number (NIN) to confirm account ownership, improve user safety, and reduce fraud on the platform.',
          ]}
        />

        <LegalSection
          icon={<FileText size={18} />}
          title="Automatically collected information"
          points={[
            'We may temporarily store technical visit information for site management and security purposes.',
            'This can include your IP address, browser type, operating system, access time, pages visited, and referring site URL.',
            'We may also collect information about viewed content in the event of a known security or virus threat.',
          ]}
        />

        <LegalSection
          icon={<FileText size={18} />}
          title="How we use your information"
          points={[
            'To process transactions, communicate status updates, and provide customer support.',
            'To improve platform operations, monitor security, and support educational communication.',
            'To comply with legal, regulatory, and fraud-prevention obligations where required.',
            'To verify identity details such as NIN information where PayRocky requires account verification before sensitive crypto transactions are completed.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="How we protect your data"
          points={[
            'We limit access to customer data to authorized personnel only.',
            'We use reasonable administrative and technical safeguards to protect stored information.',
            'We review transaction activity manually where needed to reduce fraud and unauthorized use.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Children under thirteen years of age"
          points={[
            'PayRocky does not knowingly collect personal information from children under the age of thirteen.',
            'If you are under the age of thirteen, you must ask your parent or guardian for permission to use this website.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Opt-out and unsubscribe"
          points={[
            'PayRocky respects your privacy and gives you an opportunity to opt out of receiving information related to the services.',
            `You can contact us at ${supportEmail} for opt-out or privacy-related requests.`,
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Changes to this Privacy Policy"
          points={[
            'The terms contained in this Privacy Policy may change from time to time.',
            'Any updates take effect immediately when published by the Company.',
            'You are advised to review this Privacy Policy occasionally for changes.',
          ]}
        />
      </PageContainer>
    </PublicLayout>
  );
}

export function TermsPage() {
  const supportEmail = useSupportEmail(SUPPORT_EMAIL);
  return (
    <PublicLayout>
      <PageContainer style={{ maxWidth: '980px' }}>
        <SectionHeading
          tag="Terms & Conditions"
          title="Terms and conditions"
          subtitle="These terms govern the use of PayRocky services, transactions, and community participation on the platform."
        />

        <LegalSection
          icon={<Scale size={18} />}
          title="Introduction"
          points={[
            'Thank you for using PayRocky.com, a platform operated by LordRock Icon Ltd.',
            'By placing an order, using the website, or accessing related services, you agree to these terms and conditions.',
            'If you use the platform on behalf of a business or another entity, you confirm that you have authority to bind that entity to this agreement.',
            'These terms, together with the Privacy Policy and any written notices from PayRocky, form the complete agreement between you and the Company.',
            'PayRocky may update these terms from time to time. Continued use of the platform after an update means you accept the revised terms.',
            'These terms are governed by the laws of Nigeria.',
          ]}
        />

        <LegalSection
          icon={<Scale size={18} />}
          title="Use of the platform"
          points={[
            'You are responsible for providing accurate account, wallet, and payment details.',
            'PayRocky may review, delay, or reject transactions that appear suspicious, incomplete, or non-compliant.',
            'You must use only wallet addresses and bank details that you control or are authorized to use.',
          ]}
        />

        <LegalSection
          icon={<FileText size={18} />}
          title="Transactions and pricing"
          points={[
            'Rates shown on the platform are subject to change until an order is placed and confirmed.',
            'Rate locks apply only for the stated period and may expire if payment or transfer is delayed.',
            'Incorrect payment amounts, wrong wallet details, or unsupported networks may delay or prevent completion.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Risk and limitation"
          points={[
            'Cryptocurrency transactions involve market and network risks.',
            'Blockchain transfers are irreversible once sent to the wrong address or network.',
            'By using PayRocky, you acknowledge these risks and agree to follow platform instructions carefully.',
          ]}
        />

        <LegalSection
          icon={<ShieldCheck size={18} />}
          title="Contact and support"
          points={[
            'The Company welcomes your questions or comments regarding these terms.',
            `Email: ${supportEmail}`,
            'Phone: 08127925189',
          ]}
        />
      </PageContainer>
    </PublicLayout>
  );
}

function ContactCard({ icon, title, body, actionLabel, onClick }) {
  return (
    <Card>
      <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'var(--clr-accent-dim)', color: 'var(--clr-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
        {icon}
      </div>
      <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--clr-text)', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '14px', color: 'var(--clr-text-2)', lineHeight: 1.75, marginBottom: '14px', wordBreak: 'break-word' }}>{body}</div>
      <Button variant="primary" onClick={onClick}>{actionLabel}</Button>
    </Card>
  );
}

function LegalSection({ icon, title, points }) {
  return (
    <Card style={{ marginBottom: '14px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '12px', background: 'var(--clr-accent-dim)', color: 'var(--clr-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ fontSize: '18px', fontWeight: 800, color: 'var(--clr-text)', lineHeight: 1.35 }}>{title}</div>
      </div>
      <div style={{ display: 'grid', gap: '10px' }}>
        {points.map((point) => (
          <div key={point} style={{ fontSize: '14px', color: 'var(--clr-text-2)', lineHeight: 1.75, wordBreak: 'break-word' }}>
            {point}
          </div>
        ))}
      </div>
    </Card>
  );
}
